import { useEffect, useState } from "react";
import { generatePath, useOutlet } from "react-router-dom";
import { ProductModal } from "../../components/ProductModal";

import { useTranslation } from "react-i18next";
import { Box, GradientLink, Link } from "../../common/components";
import GradientButton from "../../common/components/Button/GradientButton";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import {
  DashboardToolbarDesktopHeight,
  DashboardToolbarMobileHeight,
} from "../../components/AppToolbar/AppToolbar";
import {
  DesignCard,
  DesignCardSkeleton,
} from "../../components/Design/DesignCard";
import {
  DESIGN_CARD_DESKTOP_MIN_WIDTH,
  DESIGN_CARD_MOBILE_MIN_WIDTH,
} from "../../components/Design/DesignCard/DesignCard";
import DesignWithErrorsCard from "../../components/Design/DesignWithErrorsCard";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import { createStyles, spacing } from "../../styles/theme";
import { ROUTES } from "../../constant/common.constant";
import { useGlobalLoading } from "../../utils/ContextHooks";
import {
  getScreenHeightMinusSpacing,
  getScreenWidthMinusSpacing,
} from "../../utils/themeUtils";
import ProjectTopSection from "./ProjectTopSection";
import { useProject } from "../../context/Project/useProject";
import { useAuth } from "../../common/hooks/useAuth";
import useExternalRoute from "../../utils/useExternalRoute";
import { sortDesigns } from "../../utils/SortingUtils";
import CreateMockupModal from "../../components/Design/DesignCard/components/CreateMockupModal";
import { useDesign } from "../../context/Design/useDesign";

const styles = createStyles({
  container: {
    width: "100%",
    maxWidth: {
      lg: getScreenWidthMinusSpacing(
        spacing.largeDesktop,
        spacing.largeDesktop
      ),
    },
    maxHeight: {
      xs: getScreenHeightMinusSpacing(DashboardToolbarMobileHeight),
      lg: getScreenHeightMinusSpacing(DashboardToolbarDesktopHeight),
    },
    overflowY: "auto",
    overflowX: "hidden",
  },
  pageContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    pt: { xs: "16px", lg: "16px" },
    px: { xs: spacing.wrapperMobile, lg: spacing.largeDesktop },
    pb: { xs: spacing.xlMobile, lg: spacing.xlDesktop },
    overflowX: "hidden",
  },
  projectName: {
    mb: { xs: spacing.groupingMobile, lg: spacing.groupingDesktop },
  },
  buttonContainer: {
    pb: { xs: "16px", lg: "24px" },
    gap: { xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop },
    display: "flex",
    alignItems: "center",
    overflowX: "auto",
  },
  designsContainer: {
    mt: { xs: "16px", lg: "24px" },
    display: "grid",
    gridTemplateColumns: {
      xs: `repeat(auto-fill,minmax(${DESIGN_CARD_MOBILE_MIN_WIDTH}, 1fr))`,
      lg: `repeat(auto-fill,minmax(${DESIGN_CARD_DESKTOP_MIN_WIDTH}, 1fr))`,
    },
    gap: {
      xs: `${spacing.regularMobile} ${spacing.groupingMobile}`,
      lg: `${spacing.regularDesktop} ${spacing.groupingDesktop}`,
    },
  },
  backToTeamContainer: {
    pt: { xs: "8px", lg: "16px" },
    px: { xs: "8px", lg: "16px" },
  },
});

interface Props {
  showNewDesignModal?: boolean;
}

const Project = ({ showNewDesignModal = false }: Props) => {
  const { t } = useTranslation();
  const { userInfo } = useAuth();
  const { TRANSLATED_EXTERNAL_ROUTE } = useExternalRoute();
  const [isProductModalOpen, setIsProductModalOpen] =
    useState(showNewDesignModal);
  const { isGloballyLoading } = useGlobalLoading();
  const outlet = useOutlet();
  const { isAppLoading } = useAppLoading();
  const [showCreateMockupModal, setShowCreateMockupModal] = useState(false);
  const { project, isProjectLoading, refetchProject, projectId } = useProject();
  const { createDesign, refetchDesign, designId, design } = useDesign();
  usePageTitle("pages.projects.title", { id: project?.name });

  const addDesign = () => {
    setIsProductModalOpen(true);
  };

  const onCloseProductPopup = () => {
    setIsProductModalOpen(false);
  };

  useEffect(() => {
    refetchProject({ skipLoading: projectId === project?.id });
  }, [refetchProject, project?.id, projectId]);

  const favoriteDesigns = project?.designs?.filter(
    (design) => design.isFavorite
  );

  useEffect(() => {
    if (outlet && designId) {
      refetchDesign({ skipLoading: designId === design?.id });
    }
    // dont include outlet in the deps, because it will cause a infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchDesign, design?.id, designId]);

  if (outlet) {
    return outlet;
  }

  return (
    <Box sx={styles.container}>
      {!!project?.team_id && (
        <Box fullWidth sx={styles.backToTeamContainer}>
          <Box maxWidth={"fit-content"}>
            <Link
              data-test="project.backToTeam"
              startIcon="keyboard_arrow_left"
              to={generatePath(ROUTES.TEAM, {
                teamId: project.team_id,
              })}
            >
              {t("project.backToTeam")}
            </Link>
          </Box>
        </Box>
      )}
      <Box sx={styles.pageContent}>
        <Box sx={styles.projectName}>
          <ProjectTopSection
            project={project}
            canDeleteProject={userInfo.canDeleteProject}
            canRenameProject={userInfo.canRenameProject}
          />
        </Box>
        <Box sx={styles.buttonContainer}>
          {userInfo.canAddDesign && (
            <GradientButton
              text={t("design.new_design")}
              data-test="project.newDesign"
              icon={"add"}
              onClick={addDesign}
              textSx={{
                fontSize: { xs: "12px", lg: "16px" },
                lineHeight: { xs: "16px", lg: "auto" },
                display: "flex",
                whiteSpace: "nowrap",
              }}
            />
          )}
          <GradientLink
            text={t("navigation.work_with_designer")}
            icon={"palette"}
            to={TRANSLATED_EXTERNAL_ROUTE.WORK_WITH_DESIGNER}
            target="_blank"
            sx={[{ minWidth: "150px" }]}
            textSx={{
              fontSize: { xs: "12px", lg: "16px" },
              lineHeight: { xs: "16px", lg: "auto" },
              display: "flex",
            }}
          />
          <GradientButton
            text={t("project.download_pdf")}
            data-test="project.download_pdf"
            icon={"download"}
            onClick={() => {
              setShowCreateMockupModal(true);
            }}
            textSx={{
              fontSize: { xs: "12px", lg: "16px" },
              lineHeight: { xs: "16px", lg: "auto" },
              display: "flex",
            }}
          />
        </Box>
        <Box sx={styles.designsContainer} fullWidth>
          {isProjectLoading || isGloballyLoading ? (
            <DesignCardSkeleton />
          ) : (
            project?.designs &&
            project.designs.sort(sortDesigns).map((design) =>
              //If design is in error, then we can only delete it
              design.errorKey ? (
                <DesignWithErrorsCard
                  key={design.id}
                  design={design}
                  project={project}
                  canDeleteDesign={userInfo.canDeleteDesign}
                />
              ) : (
                <DesignCard
                  key={design.id}
                  design={design}
                  hideProjectName
                  projectName={project?.name}
                  onUpdate={() => {
                    return Promise.resolve(
                      refetchProject({
                        skipLoading: true,
                      })
                    );
                  }}
                />
              )
            )
          )}
        </Box>
      </Box>
      <ProductModal
        open={isProductModalOpen && !isAppLoading}
        createDesign={createDesign}
        onCloseModal={onCloseProductPopup}
      />
      {project?.team_id && (
        <CreateMockupModal
          open={showCreateMockupModal}
          onClose={() => setShowCreateMockupModal(false)}
          teamId={project.team_id}
          designs={
            favoriteDesigns?.map((design) => ({
              designId: design.id,
              designName: design.name,
              images: design.images || [],
              lockedVersion: design.lockedVersion,
              family: design.family,
              product: design.product,
              productLine: design.productLine,
              decorations: design.design?.decorations,
            })) ?? []
          }
          projectName={project.name || ""}
        />
      )}
    </Box>
  );
};

export default Project;
